import PropTypes from 'prop-types'
import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { RECEIVE_ALL_CLIENTS_SUCCESS } from '../../pages/hauler/clients/action/clientAction'
import styles from './index.module.scss'
import Spinner from 'react-spinkit'
import cn from 'classnames'
import SearchSuggestInput from '../search-suggest-input'
import { individualMatches, SAME_AS_BILLING_ADDRESS } from '../search-suggest-input/individualMatches'
import { jobSuggestionSearchQuery } from '../../graphql/queries/jobsSearch'
import clientsAll from '../../graphql/queries/clientsAll'
import QUERY_KEYS from '../../graphql/queryKeys'
import { captureError } from '../../utilities/errorHandlers'
import { JOB_SUGGESTION_LIMIT, loadJobFieldSuggestions } from './utils'

const SUGGESTION_LIMIT = JOB_SUGGESTION_LIMIT
const SUGGESTION_SORT = { column: 'customId', direction: 'desc' }

const initialVals = {
  universalSearch: '',
  customId: '',
  streetAddress: '',
  city: '',
  zipCode: '',
  clientId: '',
  accountName: '',
  status: ''
}

function composedAddress (parts) {
  return parts
    .map((part) => String(part ?? '').trim())
    .filter((part) => part && part.toLowerCase() !== SAME_AS_BILLING_ADDRESS)
    .join(', ')
}

function addressSuggestion (text) {
  const value = String(text ?? '').trim()
  if (!value || value.toLowerCase() === SAME_AS_BILLING_ADDRESS) return null
  return value
}

function composedAddressSuggestion (record) {
  const street = String(record.addressline1 ?? '').trim()
  const text = composedAddress([record.addressline1, record.city, record.state, record.zip])
  if (!text || text === street) return null
  return text
}

function suggestion (id, label, value, apply) {
  if (value === null || value === undefined || String(value).trim() === '') return null

  return {
    id: String(id),
    label: String(label || value),
    value,
    apply
  }
}

export default function JobsTableSearchForm ({
  clearFormValues,
  clients,
  handleSubmit,
  initialFormVals = initialVals,
  handleExportJobs,
  jobsExporting
}) {
  const dispatch = useDispatch()
  const haulerId = useSelector(({ user }) => user.user?.haulerId)
  const dataLoaders = useSelector(({ dataLoaders }) => dataLoaders)
  const [suggestionClients, setSuggestionClients] = useState([])
  const [accountNameTouched, setAccountNameTouched] = useState(false)
  const [formValues, setFormValues] = useState({
    ...initialVals,
    ...initialFormVals,
    universalSearch: initialFormVals.universalSearch || ''
  })

  useEffect(() => {
    if (!dataLoaders?.dispatcher || !haulerId) return undefined

    let cancelled = false
    clientsAll({
      dataLoaders,
      queryKey: [QUERY_KEYS.clientsAll, haulerId, true]
    })
      .then((response) => {
        if (cancelled) return
        const accounts = response?.clientsAll || []
        setSuggestionClients(accounts)
        dispatch({
          type: RECEIVE_ALL_CLIENTS_SUCCESS,
          payload: {
            clients: accounts.map((client) => ({
              id: client.id,
              name: client.name
            }))
          }
        })
      })
      .catch((error) => {
        if (cancelled) return
        setSuggestionClients([])
        captureError(error)
      })

    return () => {
      cancelled = true
    }
  }, [dataLoaders, dispatch, haulerId])

  function handleClear () {
    setAccountNameTouched(false)
    clearFormValues()
    setFormValues(initialVals)
  }

  function submitWith (patch) {
    const next = { ...formValues, ...patch }
    setFormValues(next)
    handleSubmit(next)
  }

  function handleOnChange (field, val) {
    setFormValues(prev => ({
      ...prev,
      [field]: val
    }))
  }

  function onSubmit (event) {
    event.preventDefault()
    submitSearch(formValues)
  }

  function submitSearch (values) {
    if (!accountNameTouched) {
      setFormValues(values)
      handleSubmit(values)
      return
    }

    const text = String(values.accountName || '').trim()
    if (!text) {
      const next = { ...values, accountName: '', clientId: '' }
      setAccountNameTouched(false)
      setFormValues(next)
      handleSubmit(next)
      return
    }

    const matches = (clients || []).filter((client) => {
      return String(client.name || '').trim().toLowerCase() === text.toLowerCase()
    })
    if (matches.length !== 1) {
      setFormValues(values)
      handleSubmit(values)
      return
    }

    const next = {
      ...values,
      clientId: matches[0].id,
      accountName: matches[0].name
    }
    setAccountNameTouched(false)
    setFormValues(next)
    handleSubmit(next)
  }

  function accountSuggestions (term) {
    const needle = term.trim().toLowerCase()

    return (clients || [])
      .filter((client) => client?.name?.toLowerCase().includes(needle))
      .slice(0, SUGGESTION_LIMIT)
      .map((client) => suggestion(
        `client-${client.id}`,
        client.name,
        client.id,
        { clientId: client.id, accountName: client.name }
      ))
      .filter(Boolean)
  }

  function addMatches (records, term, fieldsFor, idPrefix, matches, seen) {
    records.forEach((record) => {
      const fields = fieldsFor(record).filter((field) => field && String(field.text ?? field).trim())
      const applyByLabel = new Map(fields.map((field) => {
        const text = String(field.text ?? field).trim()
        return [text.toLowerCase(), field.apply || {}]
      }))
      individualMatches(term, fields.map((field) => field.text ?? field), [SAME_AS_BILLING_ADDRESS]).forEach((label) => {
        const key = String(label).toLowerCase()
        if (matches.length >= SUGGESTION_LIMIT || seen.has(key)) return
        const item = suggestion(`${idPrefix}-${record.id}-${label}`, label, label, applyByLabel.get(key) || {})
        if (!item) return
        seen.add(key)
        matches.push(item)
      })
    })
  }

  async function loadUniversalSuggestions (term) {
    const matches = []
    const seen = new Set()

    if (dataLoaders?.dispatcher && haulerId) {
      try {
        const response = await jobSuggestionSearchQuery({
          dataLoaders,
          queryKey: [
            QUERY_KEYS.jobsSearch,
            haulerId,
            SUGGESTION_LIMIT,
            SUGGESTION_SORT,
            { searchTerm: term.trim() },
            null
          ]
        })
        const jobs = response?.jobs?.nodes || []
        addMatches(jobs, term, (job) => [
          job.customId,
          addressSuggestion(job.addressline1),
          job.addressDetails,
          addressSuggestion(job.city),
          job.state,
          addressSuggestion(job.zip),
          job.client?.name && { text: job.client.name, apply: { clientId: job.client.id, accountName: job.client.name } },
          composedAddressSuggestion(job)
        ], 'job', matches, seen)
      } catch (error) {
        captureError(error)
      }
    }

    addMatches(suggestionClients, term, (client) => [
      client.customId,
      client.name && { text: client.name, apply: { clientId: client.id, accountName: client.name } },
      client.firstName,
      client.lastName,
      addressSuggestion(client.addressline1),
      addressSuggestion(client.city),
      client.state,
      addressSuggestion(client.zip),
      composedAddressSuggestion(client)
    ], 'client', matches, seen)

    return matches
  }

  function loadAccountSuggestions (term) {
    return Promise.resolve(accountSuggestions(term))
  }

  const selectedAccountName = clients?.find((client) => `${client.id}` === `${formValues.clientId}`)?.name || ''
  const accountName = accountNameTouched
    ? (formValues.accountName || '')
    : (selectedAccountName || formValues.accountName || '')

  function selectSuggestField (field, label, apply = {}) {
    const next = { ...formValues, ...apply, [field]: label }
    setFormValues(next)
    submitSearch(next)
  }

  function renderSuggestField (id, label, field) {
    return (
      <div className='col-xs-12 col-sm-12 col-md-3 col-lg-3 form-group'>
        <label htmlFor={id} className='control-label'>{label}</label>
        <SearchSuggestInput
          id={id}
          name={id}
          value={formValues[field] ? String(formValues[field]) : ''}
          onChange={(val) => handleOnChange(field, val)}
          onSelect={(item) => selectSuggestField(field, item.label || item.value, item.apply)}
          loadSuggestions={(term) => loadJobFieldSuggestions({ dataLoaders, haulerId, term, field })}
          className='form-control'
        />
      </div>
    )
  }

  return (
    <div className='container-fluid'>
      <form onSubmit={onSubmit} autoComplete='off'>
        <div className='row'>
          <div className='col-xs-12 form-group'>
            <label htmlFor='jobs_universal_search' className='control-label'>Search</label>
            <SearchSuggestInput
              id='jobs_universal_search'
              name='universalSearch'
              value={formValues.universalSearch || ''}
              placeholder='Search by job ids, addresses, and account names'
              onChange={(val) => handleOnChange('universalSearch', val)}
              onSelect={(item) => selectSuggestField('universalSearch', item.label || item.value, item.apply)}
              loadSuggestions={loadUniversalSuggestions}
              className='form-control'
            />
          </div>
        </div>

        <div className='row'>
          {renderSuggestField('custom_id', 'Job ID', 'customId')}
          {renderSuggestField('street_address', 'Street Address', 'streetAddress')}
          {renderSuggestField('city', 'City', 'city')}
          {renderSuggestField('zip', 'Postal Code', 'zipCode')}

          <div className='col-xs-12 col-sm-12 col-md-3 col-lg-3 form-group'>
            <label htmlFor='job_account_name' className='control-label'>Account Name</label>
            <SearchSuggestInput
              id='job_account_name'
              name='job_account_name'
              value={accountName}
              onChange={(val) => {
                setAccountNameTouched(true)
                setFormValues(prev => ({ ...prev, accountName: val }))
              }}
              onSelect={(item) => {
                setAccountNameTouched(false)
                submitWith(item.apply)
              }}
              loadSuggestions={loadAccountSuggestions}
              className='form-control'
            />
          </div>

          <div className='col-xs-12 col-sm-12 col-md-3 col-lg-3 form-group'>
            <label htmlFor='status' className='control-label'>Job Status</label>
            {/* eslint-disable-next-line jsx-a11y/no-onchange */}
            <select
              name='status'
              className='form-control'
              value={formValues.status || ''}
              onChange={(e) => handleOnChange('status', e.currentTarget.value)}>
              <option key='placeholder' value=''></option>
              {Object.entries({
                open: 'Open',
                closed: 'Closed'
              }).map(([key, value]) =>
                <option key={key} value={key}>{value}</option>)}
            </select>
          </div>

        </div>

        <div className='row text-right'>
          <button
            type='button'
            tabIndex={-1}
            onClick={handleExportJobs}
            className={cn('dis-btn-export', styles.exportButton)}>
            {jobsExporting
              ? (<Spinner name='circle' className='mx-auto' color='white' fadeIn='none' />)
              : (
                <>
                  <span className='pull-left'>Export</span>
                  <i className='material-icons pull-right space-left'>get_app</i>
                </>)}
          </button>

          <button
            type='button'
            tabIndex={-1}
            onClick={handleClear}
            className='btn btn-default push-down-very-small pull-right'
            style={{ marginLeft: 20 }}>
            <span className='pull-left'>Clear</span>
            <i className='material-icons pull-right space-left'>clear</i>
          </button>

          <button
            type='submit'
            className='btn btn-success push-down-very-small pull-right'>
            <span className='pull-left'>Search</span>
            <i className='material-icons pull-right space-left'>search</i>
          </button>

        </div>

      </form>
    </div>
  )
}

JobsTableSearchForm.propTypes = {
  initialFormVals: PropTypes.object,
  clearFormValues: PropTypes.func,
  clients: PropTypes.array,
  handleSubmit: PropTypes.func.isRequired,
  handleExportJobs: PropTypes.func.isRequired,
  jobsExporting: PropTypes.bool.isRequired
}
